import { useState, useEffect } from "react";
import { useIsMobile } from "../../hooks/useIsMobile";
import GeneralDashboardSeparateDesktopView from "./GeneralDashboardViews/DesktopView";
import GeneralDashboardSeparateMobileView from "./GeneralDashboardViews/MobileView";
import {
  FundraiserForAccount,
  LoggedInAccount,
} from "../../Interfaces/AccountUsers";
import { getCurrentlyLoggedInUser } from "../../CommonFunctions";
import config from "../../config";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../../components/Common/LoadingSpinner";

export const endDateFilter = {
  active: "Active Only",
  "6_months": "Ended within 6 Months",
  "12_months": "Ended within 12 Months",
  unrestricted: "All",
};

export const filterByOptions = {
  endDate: "End Date",
  fundraiserName: "Fundraiser Name",
};

const GeneralDashboard = () => {
  const isMobile = useIsMobile();
  const [accountID, setAccountID] = useState(null);
  const [error, setError] = useState("");
  const [loggedInAccount, setLoggedInAccount] = useState<LoggedInAccount>(
    {} as LoggedInAccount,
  );
  const [activeFundraisers, setActiveFundraisers] = useState<
    FundraiserForAccount[]
  >([]);
  const [filterView, setFilterView] = useState<string | undefined>("active");
  const [filterBy, setFilterBy] = useState<string | undefined>("endDate");
  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchActiveFundraisers = async () => {
      if (loggedInAccount.id) {
        setLoading(true);
        const token = localStorage.getItem("accessToken");

        if (!token) {
          setError("User is not authenticated");
          setLoading(false);
          return;
        }
        try {
          const [response] = await Promise.all([
            fetch(
              `${config.API_BASE_URL}account/general-dashboard/all-fundraisers/${loggedInAccount.id}?filterStatus=${filterView}&filterBy=${filterBy}`,
              {
                method: "GET",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              },
            ),
            new Promise((resolve) => setTimeout(resolve, 300)),
          ]);
          if (response.ok) {
            const data = await response.json();
            setActiveFundraisers(data);
          } else {
            const errorData = await response.json();
            setError(errorData.detail || "Failed to fetch active fundraisers");
          }
        } catch (err) {
          setError("An error occurred. Please try again.");
        } finally {
          setLoading(false);
        }
      }
    };
    fetchActiveFundraisers();
  }, [loggedInAccount.id, filterView, filterBy]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const user = await getCurrentlyLoggedInUser();
        setLoggedInAccount(user);
      } catch (error) {
        navigate("/login");
      }

      const token = localStorage.getItem("accessToken");

      if (!token) {
        setError("User is not authenticated");
        return;
      }

      try {
        const response = await fetch(
          `${config.API_BASE_URL}account/user-details/`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (response.ok) {
          const data = await response.json();
          setAccountID(data.account);
        } else {
          const errorData = await response.json();
          setError(errorData.detail || "Failed to fetch user details");
        }
      } catch (err) {
        setError("An error occurred. Please try again.");
      }
    };

    fetchUserDetails();
    document.title = "User Dashboard";
  }, [loggedInAccount.id, navigate]);

  return loading ? (
    <LoadingSpinner textToDisplay="Loading Fundraiser Entries..." />
  ) : isMobile ? (
    <GeneralDashboardSeparateMobileView
      loggedInAccount={loggedInAccount}
      fundraisers={activeFundraisers}
      filterView={filterView}
      setFilterView={setFilterView}
    />
  ) : (
    <GeneralDashboardSeparateDesktopView
      loggedInAccount={loggedInAccount}
      fundraisers={activeFundraisers}
      filterView={filterView}
      setFilterView={setFilterView}
      filterBy={filterBy}
      setFilterBy={setFilterBy}
    />
  );
};

export default GeneralDashboard;
